import memoize from 'fast-memoize';
import sortBy from 'lodash.sortby';
import { DELTA_PERCENTAGE } from '../../pages/dashboard/balance-sheet/types';
import { DATA_NOT_AVAILABLE_VALUE } from '../api/api-interfaces';
import { CompanySettingsStore } from '../company-settings-store';
import { DataTypes } from '../constants/constants';
import {
  MetricCategoryId,
  MetricFormatTypeId,
  MetricGroupId,
  MetricId as GraphQLMetricId,
  MetricType,
} from '../graphql/generated/graphql-sdk';
import { IPermissionsStore } from '../permissions/permissions-store';
import { formatPercentageNumber } from '../utilFunctions/chartConfigs';
import {
  formatCurrency,
  formatNumber,
  FormatTypes,
} from '../utilFunctions/formatters';
import {
  MetricDetails,
  MetricGroupDetails,
  MetricLifecycleStageEnum,
  MetricTree,
} from '../v2/api/metrics/types';
import { metricIdNeedsEmptyCohortFilter } from '../v2/api/utils';

import { computed } from 'mobx';
import { Hasher } from '../v2/api/hasher';
import {
  metricTreeToMetricDetailsMap,
  metricTreeToMetricGroupDetailsMap,
} from '../v2/api/metrics/utils';
import { HasDependencies, hashString } from '../v2/api/types';
import { LanguageString } from '../v2/common/components/dashboards/types';
import { MetricId } from '../v2/common/components/metrics/types';
import { Languages } from '../v2/constants';
import { convertNumDaysToYearAndMonth } from '../v2/utils-display';

export class MetricStore implements HasDependencies {
  public constructor(
    readonly permissionsStore: IPermissionsStore,
    readonly companySettingsStore: CompanySettingsStore,
    readonly selectedLanguage: Languages,
    readonly metricTree: MetricTree
  ) {}

  public getDeps = () => [
    hashString([
      ...this.permissionsStore.getDeps(),
      ...this.companySettingsStore.getDeps(),
      this.selectedLanguage,
      Hasher.hash(this.metricTree),
    ]),
  ];

  @computed
  public get metricCategoryToSortPositionMap(): Record<
    MetricCategoryId,
    number
  > {
    return Object.fromEntries(
      this.metricTree.categoryDetails.map((category) => {
        return [category.id, category.defaultPosition];
      })
    ) as Record<MetricCategoryId, number>;
  }

  @computed
  public get metricGroupToSortPositionMap(): Record<MetricGroupId, number> {
    return Object.fromEntries(
      this.metricTree.groupDetails.map((group) => {
        return [group.id, group.defaultPosition];
      })
    ) as Record<MetricGroupId, number>;
  }

  @computed
  public get metricIdToSortPositionMap(): Record<MetricId, number> {
    return Object.fromEntries(
      this.metricTree.groupDetails.flatMap((group) => {
        return group.dimensions.map((d) => {
          return [d.id, d.defaultPosition];
        });
      })
    ) as Record<MetricId, number>;
  }

  @computed
  public get metricCategoryToNameMap(): Record<
    MetricCategoryId,
    LanguageString
  > {
    return Object.fromEntries(
      this.metricTree.categoryDetails.map((category) => {
        return [category.id, category.metricCategoryName];
      })
    ) as Record<MetricCategoryId, LanguageString>;
  }

  @computed
  public get metricGroupToNameMap(): Record<MetricGroupId, LanguageString> {
    return Object.fromEntries(
      this.metricTree.groupDetails.map((group) => {
        return [group.id, group.metricGroupName];
      })
    ) as Record<MetricGroupId, LanguageString>;
  }

  @computed
  public get metricIdToNameMap(): Record<MetricId, LanguageString> {
    return Object.fromEntries(
      this.metricTree.groupDetails.flatMap((group) => {
        return group.dimensions.map((d) => {
          return [d.id, d.metricName];
        });
      })
    ) as Record<MetricId, LanguageString>;
  }

  @computed
  public get allMetricGroupDetailsWithAllowedSortedMetricIds(): MetricGroupDetails[] {
    const metricDetailsWithAllowedMetricIds =
      this.metricTree.groupDetails.map((m) => {
        return {
          ...m,
          dimensions: sortBy(m.dimensions, (d) => d.defaultPosition).filter(
            (d) => {
              const isActiveOrDeprecated =
                d.lifecycleStage.__typename ===
                  MetricLifecycleStageEnum.MetricLifecycleStageActive ||
                d.lifecycleStage.__typename ===
                  MetricLifecycleStageEnum.MetricLifecycleStageDeprecated;
              const isDomainAllowed = this.domainAllowedMetricsSet.has(d.id);
              const isPermissionsAllowed = this.permissionsStore.canViewMetric(
                d.id as unknown as GraphQLMetricId
              );
              return (
                isActiveOrDeprecated && isDomainAllowed && isPermissionsAllowed
              );
            }
          ),
        };
      }) ?? [];
    return metricDetailsWithAllowedMetricIds;
  }

  @computed
  public get allMetricIds(): GraphQLMetricId[] {
    return this.metricTree.groupDetails.flatMap((group) =>
      group.dimensions.map((d) => d.id)
    );
  }

  @computed
  public get domainAllowedMetricsSet(): Set<GraphQLMetricId> {
    const domainAllowedMetricsSet = new Set(
      this.companySettingsStore.allowedMetricsForDomain
    );
    return domainAllowedMetricsSet;
  }

  @computed
  public get domainAllowedMetricGroupsSet(): Set<MetricGroupId> {
    const allMetricGroups = this.metricTree.groupDetails;
    // Domain Allowed metric groups would be those that have at least one domain allowed metric
    const domainAllowedMetricGroups = allMetricGroups
      .map((g) => {
        return {
          ...g,
          dimensions: g.dimensions.filter((d) =>
            this.domainAllowedMetricsSet.has(d.id)
          ),
        };
      })
      .filter((g) => g.dimensions.length)
      .map((g) => g.id);
    return new Set(domainAllowedMetricGroups);
  }

  @computed
  public get allMetricGroupIds(): MetricGroupId[] {
    return this.metricTree.groupDetails.map((g) => g.id);
  }

  public getTranslatedMetricCategoryName = (
    categoryId: MetricCategoryId
  ): string => {
    return (
      this.metricCategoryToNameMap?.[categoryId]?.[this.selectedLanguage] ??
      categoryId
    );
  };

  public getTranslatedMetricGroupName = (
    metricGroupId: MetricGroupId
  ): string => {
    return (
      this.metricGroupToNameMap?.[metricGroupId]?.[this.selectedLanguage] ??
      metricGroupId
    );
  };

  public getTranslatedMetricIdName = (metricId: GraphQLMetricId): string => {
    return (
      this.metricIdToNameMap?.[metricId]?.[this.selectedLanguage] ?? metricId
    );
  };

  public getPermissionsAllowedMetricGroups = (): MetricGroupId[] => {
    const allowedMetricGroups =
      this.allMetricGroupDetailsWithAllowedSortedMetricIds
        .filter((m) => m.dimensions.length)
        .map((m) => m.id);
    return allowedMetricGroups;
  };

  public getMetricGroupDetails = (
    metricGroupId: MetricGroupId
  ): MetricGroupDetails => {
    const metricGroupDetails = this.metricTree.groupDetails.find(
      (m) => m.id === metricGroupId
    );
    if (!metricGroupDetails) {
      throw new Error(`Metric not found - ${metricGroupId}`);
    } else {
      return metricGroupDetails;
    }
  };

  public getMetricGroupDetailsFromMetricId = memoize(
    (metric: GraphQLMetricId): MetricGroupDetails => {
      const metricDetails = metricTreeToMetricDetailsMap(this.metricTree)[
        metric
      ];
      const metricGroupDetails = metricTreeToMetricGroupDetailsMap(
        this.metricTree
      )[metricDetails.metricGroupId];
      if (!metricGroupDetails) {
        throw new Error(`Metric group details for metric ${metric} not found`);
      } else {
        return metricGroupDetails;
      }
    }
  );

  public getMetricDetailsFromMetricId = memoize(
    (metric: GraphQLMetricId): MetricDetails => {
      const metricDetails = metricTreeToMetricDetailsMap(this.metricTree)[
        metric
      ];
      if (!metricDetails) {
        throw new Error(`Metric group details for metric ${metric} not found`);
      } else {
        return metricDetails;
      }
    }
  );

  public getMetricsForMetricGroup = (
    metric: MetricGroupId
  ): MetricDetails[] => {
    const metricDetails = this.metricTree.groupDetails.find(
      (m) => m.id === metric
    );
    if (!metricDetails) {
      throw new Error(`invalid metric id - ${metric}`);
    } else {
      return metricDetails.dimensions;
    }
  };

  public getAllowedSortedMetricIdsForMetricGroup = (
    metricGroupId: MetricGroupId
  ): MetricDetails[] => {
    const metricDetails =
      this.allMetricGroupDetailsWithAllowedSortedMetricIds.find(
        (m) => m.id === metricGroupId
      );
    if (!metricDetails) {
      // TODO: This might happen if some invalid id is in store
      // should have a better handling
      throw new Error(`invalid metric group id - ${metricGroupId}`);
    } else {
      const sortedMetricIds = sortBy(
        metricDetails.dimensions,
        (d) => d.defaultPosition
      );
      return sortedMetricIds;
    }
  };

  public isMetricGroupRegular = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeRegular
    );
  };

  public isMetricGroupCohort = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeCohort
    );
  };

  public isMetricGroupEmployeeCohort = (metric: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metric).some(
      (dim) => dim.metricType === MetricType.MetricTypeEmployeeCohort
    );
  };

  public isMetricRegular = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeRegular
    );
  };

  public isMetricCohort = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeCohort
    );
  };

  public isMetricEmployeeCohort = (metric: GraphQLMetricId) => {
    return (
      this.getMetricDetailsFromMetricId(metric).metricType ===
      MetricType.MetricTypeEmployeeCohort
    );
  };

  public isMetricFinancial = (metric: MetricDetails) => {
    return metric.underlyingFields?.some(
      (f) => f.dataType === DataTypes.FINANCIALMETRICS
    );
  };

  public isMetricGroupFinancial = (metricGroup: MetricGroupId) => {
    return this.getMetricsForMetricGroup(metricGroup).some((dim) =>
      this.isMetricFinancial(dim)
    );
  };

  public metricGroupNeedsEmptyCohortFilter = (metricGroupId: MetricGroupId) => {
    const firstMetric = this.getMetricsForMetricGroup(metricGroupId)[0];
    if (!firstMetric) {
      throw new Error(`invalid metric group id - ${metricGroupId}`);
    } else {
      return metricIdNeedsEmptyCohortFilter(firstMetric.id);
    }
  };

  @computed
  public get defaultMetricGroupCategorySettings(): {
    metricGroup: MetricGroupId;
    category: MetricCategoryId;
  }[] {
    return this.metricTree.groupDetails.map((mg) => {
      return { metricGroup: mg.id, category: mg.defaultCategory };
    });
  }

  @computed
  public get effectiveMetricGroupCategorySettings(): {
    metricGroup: MetricGroupId;
    category: MetricCategoryId;
  }[] {
    const metricGroupCategorySettingsAsMap = Object.fromEntries(
      (this.companySettingsStore.metricGroupCategorySettings ?? []).map(
        (settings) => {
          return [settings.metricGroup, settings.category];
        }
      )
    );
    const defaultMetricGroupCategorySettingsAsMap = Object.fromEntries(
      (this.defaultMetricGroupCategorySettings ?? []).map((settings) => {
        return [settings.metricGroup, settings.category];
      })
    );
    const effectiveMetricGroupCategorySettings =
      this.metricTree.groupDetails.map((mg) => {
        return {
          metricGroup: mg.id,
          category:
            metricGroupCategorySettingsAsMap[mg.id] ??
            defaultMetricGroupCategorySettingsAsMap[mg.id],
        };
      });
    return effectiveMetricGroupCategorySettings;
  }

  public getCategoryForMetricGroup = (metricGroup: MetricGroupId) => {
    const domainSettingMetric = this.effectiveMetricGroupCategorySettings.find(
      (m) => m.metricGroup === metricGroup
    );
    if (!domainSettingMetric?.category) {
      throw new Error('Invalid state');
    }
    return domainSettingMetric.category;
  };

  public getCategoryForMetric = (metric: GraphQLMetricId) => {
    const metricGroupDetails = this.getMetricGroupDetailsFromMetricId(metric);
    return this.getCategoryForMetricGroup(metricGroupDetails.id);
  };

  private metricFormatTypeToFormatterMap: Record<
    MetricFormatTypeId,
    (val: any) => string
  > = {
    [MetricFormatTypeId.Pmft001Salary]: (val) => {
      const refCurrency = this.companySettingsStore.referenceCurrency() ?? '';
      return formatCurrency(val, refCurrency);
    },
    [MetricFormatTypeId.Pmft002Percentage]: (val) => {
      return formatPercentageNumber(val);
    },
    [MetricFormatTypeId.Pmft004DaysToYearMonth]: (val) => {
      return convertNumDaysToYearAndMonth(Number(val));
    },
    [MetricFormatTypeId.Pmft005Count]: (val) => {
      return formatNumber(val, FormatTypes.COUNT);
    },
    [MetricFormatTypeId.Pmft006Number]: (val) => {
      return formatNumber(val, FormatTypes.NUMBER);
    },
    [MetricFormatTypeId.Pmft007SensitiveNumber]: (val) => {
      return formatNumber(val, FormatTypes.SENSITIVE_NUMBER);
    },
    [MetricFormatTypeId.Pmft008Multiple]: (val) => {
      return `${formatNumber(val, FormatTypes.NUMBER)}x`;
    },
    [MetricFormatTypeId.Pmft009Leaves]: (val) => {
      return formatNumber(val, FormatTypes.LEAVES_TAKEN);
    },
    [MetricFormatTypeId.Pmft010Hours]: (val) => {
      return formatNumber(val, FormatTypes.HOURS);
    },
    [MetricFormatTypeId.Pmft011Fte]: (val) => {
      return formatNumber(val, FormatTypes.FTE);
    },
    [MetricFormatTypeId.Pmft012FloatingNumber1]: (val) => {
      return formatNumber(val, FormatTypes.FLOATING_NUMBER_1);
    },
    [MetricFormatTypeId.Pmft013FloatingNumber2]: (val) => {
      return formatNumber(val, FormatTypes.FLOATING_NUMBER_2);
    },
    [MetricFormatTypeId.Pmft014FloatingPercentage1]: (val) => {
      return formatPercentageNumber(val, FormatTypes.FLOATING_PERCENTAGE_1);
    },
    [MetricFormatTypeId.Pmft015FloatingPercentage2]: (val) => {
      return formatPercentageNumber(val, FormatTypes.FLOATING_PERCENTAGE_2);
    },
  };

  public formatMetricValue = (
    val: number | string | null,
    metricId: GraphQLMetricId,
    header?: string
  ) => {
    const metricFormatType =
      this.getMetricDetailsFromMetricId(metricId).formatType;
    if (val === null) {
      return DATA_NOT_AVAILABLE_VALUE;
    }
    if (header === DELTA_PERCENTAGE) {
      return formatPercentageNumber(Number(val));
    }
    return this.metricFormatTypeToFormatterMap[metricFormatType](val);
  };
}
